<template>
  <div v-if="userCanMessageFamily">
    <div class="comments-wrapper" v-if="isChatActive">
      <div class="comments-button" @click="toggleChat">
        <span>{{ $t('GeneralTranslations.Messages') }}</span>
        <v-icon class="pl-1" size="12px" color="white">mdi-triangle-down</v-icon>
      </div>
      <v-card class="chat-box">
        <div v-if="showThreadTabs" class="chat-tabs">
          <v-btn
            :class="['tab-btn', { active: activeTab === 'me' }]"
            @click="activeTab = 'me'"
            depressed
          >Me</v-btn>
          <v-btn
            :class="['tab-btn', { active: activeTab === 'home' }]"
            @click="activeTab = 'home'"
            depressed
          >
            Home
            <v-badge v-if="homeUnreadCount > 0" :content="homeUnreadCount" color="red" inline class="ml-1"></v-badge>
          </v-btn>
        </div>
        <ChatPanel v-if="showIndividualThreads && (!staffFamilyMessagingEnabled || activeTab === 'me')" :data="{ maxHeight: 'calc(100vh - 260px)', ...data }" />
        <HomeChatPanel v-if="staffFamilyMessagingEnabled && (activeTab === 'home' || !showIndividualThreads)" :data="homeChatData" />
      </v-card>
    </div>
    <div class="open-comments-button" v-else @click="toggleChat">
      <span>{{ $t('GeneralTranslations.Messages') }}</span>
      <v-badge v-if="totalUnreadCount > 0" class="v-badge-comments" :content="totalUnreadCount"
        color="red" overlap>
      </v-badge>
    </div>
  </div>
</template>

<script>
import * as firebase from "firebase/app";
import { mapGetters } from "vuex";
import ChatPanel from '@/components/chat/ChatPanel.vue'
import HomeChatPanel from '@/components/chat/HomeChatPanel.vue'
import { FeatureFlags, isFeatureEnabled } from '@/consts/featureFlags'
export default {
  name: "Chat",
  components: { ChatPanel, HomeChatPanel },
  props: {
    data: Object
  },
  data() {
    return {
      loading: true,
      unsubscribeFn: null,
      homeUnsubscribeFn: null,
      isChatActive: false,
      isFileUploading: false,
      isSending: false,
      unreadCount: 0,
      homeUnreadCount: 0,
      activeTab: 'me'
    }
  },

  computed: {
    facility() {
      return this.$store.state.authModule.currentFacilityData
    },
    staffFamilyMessagingEnabled() {
      return isFeatureEnabled(this.facility, FeatureFlags.staffFamilyMessagingEnabled)
    },
    individualThreadsEnabled() {
      if (!this.staffFamilyMessagingEnabled) return true
      return this.facility.allowStaffFamilyThreads === true
    },
    userCanMessageFamily() {
      if (this.user?.canMessageFamily !== undefined) return this.user.canMessageFamily;
      const allowedLevels = this.facility?.familyMessagingAccessLevels || [];
      return allowedLevels.length === 0 || allowedLevels.includes(this.user?.accessLevel);
    },
    showIndividualThreads() {
      return this.individualThreadsEnabled && this.userCanMessageFamily
    },
    showThreadTabs() {
      return this.staffFamilyMessagingEnabled && this.showIndividualThreads
    },
    homeChatData() {
      return {
        familyMember: this.data.familyMember,
        channel: this.data.familyMember?.id || '',
        maxHeight: 'calc(100vh - 260px)'
      }
    },
    totalUnreadCount() {
      return this.unreadCount + this.homeUnreadCount
    },
    dateTimeFormat() {
      return `${this.facility.dateFormat} ${this.facility.timeFormat}`
    },
    channel() {
      return `${this.user.id}${this.data.familyMember.id}`
    },
    ...mapGetters({
      user: "user"
    })
  },

  mounted() {
    if (this.staffFamilyMessagingEnabled) {
      this.subscribeToHomeUnreadCount();
    }
    if (this.showIndividualThreads) {
      this.subscribeToUnreadCount();
      this.activeTab = 'me';
    } else if (this.staffFamilyMessagingEnabled) {
      this.activeTab = 'home';
    }
  },

  beforeDestroy() {
    if (this.unsubscribeFn) {
        this.unsubscribeFn();
        this.closeChat();
    }
    if (this.homeUnsubscribeFn) {
        this.homeUnsubscribeFn();
    }
  },

  async created(){
    await this.initChat()
  },

  methods: {
    async toggleChat() {
      this.isChatActive = !this.isChatActive;

      if (!this.isChatActive) {
        this.closeChat()
      }
    },

    subscribeToUnreadCount() {
        const query = firebase.firestore()
            .collection("messages")
            .where("facilityId", "==", this.facility.id)
            .where('channel', '==', this.channel)
            .where("to", "==", "staff")
            .where("read", "==", false);

        this.unsubscribeFn = query.onSnapshot(snapshot => {
            this.unreadCount = snapshot.size;
        });
    },

    subscribeToHomeUnreadCount() {
        const query = firebase.firestore()
            .collection("homeChatsMessage")
            .where("facilityId", "==", this.facility.id)
            .where('channel', '==', this.data.familyMember?.id)
            .where("senderRole", "==", "family")
            .where("read", "==", false);

        this.homeUnsubscribeFn = query.onSnapshot(snapshot => {
            this.homeUnreadCount = snapshot.size;
        });
    },

    async initChat() {
      this.loading = false
    },

    closeChat() {
      console.log('closeChat')
      this.loading = false
      this.isChatActive = false
    },
  }
}
</script>

<style scoped lang="scss">
.v-card {
  padding: initial !important;
  min-height: initial !important;
  background-color: #fff !important;
}

.messages-form {
  bottom: 0;
  width: -webkit-fill-available;
  margin-top: auto;
}

.no-shadow {
  box-shadow: none !important;
}

.chat-message {
  display: unset !important;
  white-space: break-spaces;
}

.chat-screen {
  max-height: 320px;
  overflow-y: auto;
}

.flex-none {
  flex: unset;
}

.received-message,
.sent-message {
  line-break: anywhere;
  cursor: text;
}

.received-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: 54px;
  right: auto;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #4caf50 transparent transparent transparent;
}

.sent-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: auto;
  right: 54px;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #3f51b5 transparent transparent transparent;
}

.comments-button {
  position: fixed;
  top: 70px;
  right: 430px;
  width: 140px;
  height: 50px;
  background-color: #3f51b5;
  transform-origin: top;
  transform: rotate(-90deg);
  color: white;
  text-align: center;
  line-height: 50px;
  transition: right 0.25s;
  cursor: pointer;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  outline: none;
}

@media screen and (max-width: 800px) {
  .comments-button {
    position: static;
    width: 100%;
    max-width: 350px;
    margin: auto;
    height: 50px;
    transform: rotate(0deg);
  }
}

.chat-box {
  width: 450px;
  height: 100%;
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  box-shadow: none !important;
}

.chat-content {
  list-style: none;
  padding: 0;
  color: #fff;
}

.open-comments-button {
  bottom: 65vh;
  position: fixed;
  right: 0px;
  width: 100px;
  height: 60px;
  background-color: #3f51b5;
  transform-origin: center top;
  transform: rotate(-90deg);
  color: white;
  text-align: center;
  line-height: 50px;
  transition: right 0.25s;
  cursor: pointer;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  outline: none;
}

.open-comments-button:hover,
.open-comments-button:focus {
  right: 10px
}

.open-comments-button.active+.chat-box {
  right: 0;
}

@media screen and (max-width: 800px) {
  .open-comments-button {
    position: static;
    width: 100%;
    max-width: 350px;
    margin: auto;
    height: 50px;
    transform: rotate(0deg);
  }
}

.v-badge.v-badge-comments {
  transform: rotate(90deg);
  position: absolute;
  margin-left: 10px;
}

.chat-tabs {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 6px;
  gap: 4px;
  flex-shrink: 0;
}

.tab-btn {
  flex: 1;
  height: 34px !important;
  font-size: 13px !important;
  border-radius: 4px !important;
  text-transform: none !important;
  font-weight: 600;
  letter-spacing: 0 !important;
  color: #666 !important;
  background-color: #f5f5f5 !important;
}

.tab-btn.active {
  background-color: #3f51b5 !important;
  color: white !important;
}
</style>